import { useEffect, useRef, useState } from 'react'
import { animate } from 'motion/react'

import { BillCard } from '@/components/legislative-tracker/BillCard'
import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'

import type { Bill, Topic } from '@/payload-types'

// const CARD_WIDTH = 394
const CARD_HEIGHT = 178
const CARD_GAP = 8
// const CARD_COUNT = 3
const CLOSED_Y_OFFSETS = [0, -164, -328, -520]

type variantProps = {
  y: number
  height?: number | 'auto'
  scale?: number
  opacity?: number
  pointerEvents?: string
  cursor?: string
  boxShadow?: string
  filter?: string
}

type Variants = {
  open: variantProps
  closed: variantProps
}

// Stack animations
const stackVariants: Variants = {
  open: {
    y: 20,
    height: 'auto',
    cursor: 'pointer',
    filter: 'drop-shadow(0px 0px 0px rgba(0,0,0,0.0))',
  },
  closed: {
    y: 0,
    height: 204,
    cursor: 'default',
    filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.05))',
  },
}

// Header animations
const headerVariants: Variants = {
  open: {
    y: 0,
    scale: 1,
    opacity: 1,
  },
  closed: {
    y: 60,
    scale: 0.8,
    opacity: 0,
  },
}

export const BillCardStack = ({
  bills,
  allBillTopics,
  renderOpen = false,
  useBillProgress = false,
}: {
  bills: Bill[]
  allBillTopics: Topic[]
  renderOpen?: boolean
  useBillProgress?: boolean
}) => {
  const [isOpen, setIsOpen] = useState(renderOpen)
  const stackRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const headerButtonRef = useRef<HTMLButtonElement>(null)
  const cardRefs = useRef<HTMLElement[]>([])

  function animateStack(state: keyof Variants, instant = false) {
    if (!stackRef.current) return
    animate(stackRef.current, stackVariants[state], {
      type: instant ? 'tween' : 'spring',
      mass: instant ? undefined : 0.7,
      duration: instant ? 0 : undefined,
    })
  }

  function animateHeader(state: keyof Variants, instant = false) {
    if (!headerRef.current) return
    animate(headerRef.current, headerVariants[state], {
      type: instant ? 'tween' : 'spring',
      stiffness: instant ? undefined : 600,
      damping: instant ? undefined : 50,
      delay: instant ? 0 : state === 'open' ? 0.2 : 0,
      duration: instant ? 0 : undefined,
    })
  }

  function animateCard(
    element: HTMLElement,
    index: number,
    state: keyof Variants,
    instant = false,
  ) {
    const closedY = CLOSED_Y_OFFSETS[index] ?? (CARD_HEIGHT - CARD_GAP) * -index + CARD_GAP * index
    const variants: Variants = {
      open: {
        y: 0,
        scale: 1,
        opacity: 1,
        pointerEvents: 'auto',
        cursor: 'pointer',
      },
      closed: {
        y: closedY,
        scale: index === 0 ? 1 : 1 - index * 0.1,
        opacity: index === 0 ? 1 : 1 - index * 0.1,
        pointerEvents: 'none',
        cursor: 'default',
      },
    }

    animate(element, variants[state], {
      type: instant ? 'tween' : 'spring',
      stiffness: instant ? undefined : 600,
      damping: instant ? undefined : 50,
      delay: instant ? 0 : index * 0.04,
      duration: instant ? 0 : undefined,
    })
  }

  useEffect(() => {
    const state = isOpen ? 'open' : 'closed'
    animateStack(state, true)
    animateHeader(state, true)
    cardRefs.current.forEach((card, index) => {
      if (card) {
        animateCard(card, index, state, true)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount/bills change, not isOpen toggle (handled by toggle functions)
  }, [bills.length])

  function toggleCollapse() {
    setIsOpen(false)
    animateStack('closed')
    animateHeader('closed')

    cardRefs.current.forEach((card, index) => {
      if (card) {
        animateCard(card, index, 'closed')
      }
    })
  }

  function toggleExpand() {
    setIsOpen(true)
    animateStack('open')
    animateHeader('open')

    cardRefs.current.forEach((card, index) => {
      if (card) {
        animateCard(card, index, 'open')
      }
    })

    setTimeout(() => {
      // headerButtonRef.current?.focus()
      cardRefs.current[0]?.focus()
    }, 200)
  }

  return (
    <div
      ref={stackRef}
      className={cn(
        'bill-card-stack relative flex w-full max-w-[406px] translate-y-5 flex-col gap-2 will-change-transform [&.bill-card-stack_+.bill-card-stack--open]:mt-4',
        '[&.bill-card-stack--open_+.bill-card-stack--open]:mt-5',
        isOpen ? 'bill-card-stack--open mb-4' : 'bill-card-stack--closed',
      )}
      // style={{ height: isOpen ? 'auto' : 204 }}
    >
      <div
        ref={headerRef}
        className={cn(
          'pointer-events-none absolute -top-10 left-0 flex h-9 w-full origin-[bottom_center] items-center justify-between gap-3 will-change-[transform,opacity]',
        )}
      >
        <h2 className="font-body text-base leading-[1em] font-semibold text-foreground">
          Featured Bills
        </h2>
        <Button
          variant="outline"
          icon={false}
          size="small"
          ref={headerButtonRef}
          className="pointer-events-auto select-none"
          onClick={toggleCollapse}
        >
          Collapse
        </Button>
      </div>

      {/* Overlay button to handle expand */}
      <button
        className={cn(
          'absolute inset-0 w-full cursor-pointer rounded-2xl border-none bg-none focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-0',
          isOpen ? 'pointer-events-none' : 'pointer-events-auto',
        )}
        tabIndex={isOpen ? -1 : 0}
        aria-disabled={isOpen}
        style={{
          height: `${CARD_HEIGHT}px`,
          zIndex: isOpen ? 0 : 10,
        }}
        onClick={toggleExpand}
      >
        <span className="sr-only">Expand</span>
      </button>

      {bills.map((bill, index) => (
        <BillCard
          ref={(el: HTMLElement | null) => {
            if (el) {
              cardRefs.current[index] = el
            }
          }}
          key={bill.id}
          bill={bill}
          allBillTopics={allBillTopics}
          size="small"
          useBillProgress={useBillProgress}
          tabIndex={isOpen ? 0 : -1}
          aria-disabled={!isOpen}
          aria-label={`View bill ${bill.title}`}
          style={{
            zIndex: bills.length - index,
            pointerEvents: isOpen ? 'auto' : 'none',
            height: `${CARD_HEIGHT}px`,
            minHeight: `${CARD_HEIGHT}px`,
            maxHeight: `${CARD_HEIGHT}px`,
          }}
        />
      ))}
    </div>
  )
}
